import { getSiteSettings, getUser } from '../providers/authService';

/**
 * Posts a client error to the API's ErrorController, which logs it and emails it.
 *
 * Deliberately does not go through UnityApi or providers/core/requests.js, for three reasons:
 *
 *  1. Recursion. UnityApi.request calls sendError from its own catch, so reporting an error
 *     through it could report the failure of that report, and so on. The first hop is easy to
 *     hit: getSiteSettings() returning null makes getUser() throw, or request() throws
 *     'No token'.
 *  2. requests.js reloads the page on a 401. Reporting an error should not navigate the user
 *     away from what they were doing.
 *  3. keepalive lets the report survive the page being unloaded, which the XHR wrapper cannot do.
 *
 * Never throws and never rejects: the error reporter must not be the thing that breaks a page.
 * Silently gives up when there is no token, because ErrorController sits behind
 * MapControllers().RequireAuthorization() -- pre-sign-in errors can only be reported through
 * Application Insights.
 */
export default function postClientError({
  type,
  errorMessage,
  stackTrace,
  url,
}) {
  try {
    const endpoint = getSiteSettings()?.unityApiEndpoint;
    if (!endpoint) return;

    const token = getUser()?.access_token;
    if (!token) return;

    fetch(`${endpoint}Error`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        type,
        errorMessage,
        stackTrace,
        url,
      }),
    }).catch(() => {
      // Nothing useful to do: Application Insights already has this error.
    });
  } catch {
    // getSiteSettings parses localStorage and getUser destructures its result; neither is
    // allowed to turn a reported error into a thrown one.
  }
}
